import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Routes from '../utils/routes.js.erb';
import { authHeaders } from '../actions';

const fetchUnreadCount = async () => {
  const response = await fetch(Routes.api_dashboard_communication_unread_count_path(), {
    headers: authHeaders(),
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('Error fetching unread count');
  }

  return response.json();
};

//
// Unread conversation count for the navigation bell.
//
// Polls rather than subscribing: the existing ActionCable connection is not
// authenticated per admin user and only streams per-player coach chats, so it
// cannot address a "this user has unread messages" event. A minute of lag is
// immaterial here — the counterpart of this bell is a *daily* email.
//
// Instant clearing when the user reads something is handled by invalidating the
// ['unreadCount'] key from communicationQueryMiddleware, so polling only needs to
// cover messages arriving from elsewhere.
//
export const useUnreadCount = ({ enabled = true } = {}) => {
  const { data } = useQuery({
    queryKey: ['unreadCount'],
    queryFn: fetchUnreadCount,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    staleTime: 30000,
    enabled,
  });

  return data?.unread_count ?? 0;
};

const markAllRead = async () => {
  const response = await fetch(Routes.api_dashboard_communication_mark_all_read_path(), {
    headers: authHeaders(),
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error('Error marking messages as read');
  }

  return response.json();
};

//
// Clears every unread marker for the current admin user. Both the count and the
// conversation lists are invalidated so the bell, its panel and the UNREAD tab all
// settle on the new state.
//
export const useMarkAllRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      queryClient.setQueryData(['unreadCount'], { unread_count: 0 });
      queryClient.invalidateQueries({ queryKey: ['unreadCount'] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
};

export default useUnreadCount;
